import React, { useState, useRef } from 'react';
import { Share2, Download, X, Copy, Check, Sparkles, Heart } from 'lucide-react';
import { useToPng, useToBlob } from '@hugocxl/react-to-image';
import { toBlob as htmlToImageToBlob } from 'html-to-image';
import toast from 'react-hot-toast';

interface ShareCardProps {
    isOpen: boolean;
    onClose: () => void;
    person1Name: string;
    person2Name: string;
    totalScore: number;
    maxScore: number;
    compatibilityLevel: string;
    person1Rashi?: string;
    person2Rashi?: string;
    description?: string;
    language: 'english' | 'hindi';
    translations: any;
}

// Shareable card component that will be converted to image
interface ShareableContentProps {
    person1Name: string;
    person2Name: string;
    totalScore: number;
    maxScore: number;
    compatibilityLevel: string;
    person1Rashi?: string;
    person2Rashi?: string;
    description?: string;
    percentage: number;
    gradient: {
        start: string;
        text: string;
        bg: string;
        border: string;
    };
    t: any;
}

const ShareableContent = React.forwardRef<HTMLDivElement, ShareableContentProps>(({
    person1Name,
    person2Name,
    totalScore,
    maxScore,
    compatibilityLevel,
    person1Rashi,
    person2Rashi,
    description,
    percentage,
    gradient,
    t
}, ref) => (
    <div
        ref={ref}
        className="w-[400px] h-[400px] relative"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
    >
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1e1b4b] via-[#5b21b6] to-[#4c1d95]" />
        <div className="absolute top-0 right-0 w-40 h-40 bg-purple-600/30 rounded-full blur-[50px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-purple-500/30 rounded-full blur-[50px] translate-y-1/2 -translate-x-1/2" />

        {/* Star Pattern */}
        <div className="absolute inset-0 opacity-20"
            style={{
                backgroundImage: 'radial-gradient(white 1px, transparent 1px)',
                backgroundSize: '20px 20px'
            }}
        />

        {/* Content - using fixed positioning */}
        <div className="absolute inset-0 flex flex-col items-center px-4 py-5">

            {/* Header Label */}
            <div className="flex items-center gap-1 bg-white/10 border border-white/20 px-3 py-1 rounded-full">
                <Sparkles size={10} className="text-amber-300" />
                <span className="text-white text-[9px] font-semibold tracking-wide uppercase">{t.shareTitle}</span>
            </div>

            {/* Names Row */}
            <div className="w-full flex items-center justify-center gap-2 mt-3 px-2">
                <div className="text-center flex-1 min-w-0">
                    <h3 className="text-[13px] font-bold text-white truncate">{person1Name}</h3>
                    {person1Rashi && <p className="text-white/50 text-[9px]">{person1Rashi}</p>}
                </div>
                <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center border border-white/20 flex-shrink-0">
                    <Heart size={12} className="text-pink-500 fill-pink-500" />
                </div>
                <div className="text-center flex-1 min-w-0">
                    <h3 className="text-[13px] font-bold text-white truncate">{person2Name}</h3>
                    {person2Rashi && <p className="text-white/50 text-[9px]">{person2Rashi}</p>}
                </div>
            </div>

            {/* Score Circle */}
            <div className="relative my-2 flex-shrink-0">
                <div className={`absolute -inset-2 rounded-full blur-lg ${gradient.bg} opacity-40`} />
                <div className="relative w-[85px] h-[85px]">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                        <circle
                            cx="50" cy="50" r="42"
                            fill="none"
                            stroke={gradient.start}
                            strokeWidth="6"
                            strokeLinecap="round"
                            strokeDasharray={`${(percentage / 100) * 264} 264`}
                            style={{ filter: `drop-shadow(0 0 5px ${gradient.start})` }}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-[20px] font-bold text-white leading-none">{totalScore}</span>
                        <span className="text-[7px] text-white/50 font-medium uppercase tracking-wider mt-0.5">
                            {t.outOf} {maxScore}
                        </span>
                    </div>
                </div>
            </div>

            {/* Compatibility Badge */}
            <div className={`px-3 py-1 rounded-lg ${gradient.bg} ${gradient.border} border`}>
                <span className={`text-[10px] font-bold ${gradient.text}`}>
                    {compatibilityLevel} • {((totalScore / maxScore) * 100).toFixed(1)}%
                </span>
            </div>

            {/* Description Card */}
            {description && (
                <div className="mt-2 mx-3 p-2.5 bg-white/[0.12] border border-white/20 rounded-lg">
                    <p className="text-white/90 text-[8px] leading-relaxed text-center">
                        {description}
                    </p>
                </div>
            )}

            {/* Footer Branding - always at bottom */}
            <div className="mt-auto pt-2 flex items-center justify-center gap-1.5">
                <img
                    src="/bhrigoo_rishi.webp"
                    alt="BhriguGPT"
                    className="w-5 h-5 object-contain"
                />
                <div className="flex flex-col">
                    <span className="text-white/60 text-[10px] font-bold tracking-wide">BhriguGPT</span>
                    <span className="text-white/40 text-[6px] uppercase tracking-[0.12em]">AI Astrologer</span>
                </div>
            </div>
        </div>
    </div>
));

ShareableContent.displayName = 'ShareableContent';

const ShareCard: React.FC<ShareCardProps> = ({
    isOpen,
    onClose,
    person1Name,
    person2Name,
    totalScore,
    maxScore,
    compatibilityLevel,
    person1Rashi,
    person2Rashi,
    description,
    language,
    translations: t
}) => {
    const [copied, setCopied] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const percentage = Math.round((totalScore / maxScore) * 100);

    const getGradientColors = () => {
        if (percentage >= 80) return { start: '#10b981', end: '#059669', text: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' };
        if (percentage >= 60) return { start: '#8b5cf6', end: '#6d28d9', text: 'text-purple-400', bg: 'bg-purple-500/20', border: 'border-purple-500/30' };
        if (percentage >= 40) return { start: '#f59e0b', end: '#d97706', text: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30' };
        if (percentage >= 20) return { start: '#f97316', end: '#ea580c', text: 'text-orange-400', bg: 'bg-orange-500/20', border: 'border-orange-500/30' };
        return { start: '#ef4444', end: '#dc2626', text: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30' };
    };

    const gradient = getGradientColors();

    // Hook for PNG download
    const [{ isLoading: isDownloading }, convertToPng, pngRef] = useToPng<HTMLDivElement>({
        quality: 1,
        pixelRatio: 2,
        onSuccess: (dataUrl) => {
            const link = document.createElement('a');
            link.download = `kundli-matching-${person1Name}-${person2Name}.png`;
            link.href = dataUrl;
            link.click();
            toast.success(t.downloadComplete);
        },
        onError: () => toast.error(t.shareError)
    });

    // Hook for Blob (share & copy)
    const [{ isLoading: isSharing }, convertToBlob, blobRef] = useToBlob<HTMLDivElement>({
        quality: 1,
        pixelRatio: 2,
    });

    const handleShare = async () => {
        try {
            const blob = await new Promise<Blob | null>((resolve) => {
                convertToBlob();
                // Small delay to let the hook process
                setTimeout(async () => {
                    const canvas = cardRef.current;
                    if (canvas) {
                        const result = await htmlToImageToBlob(canvas, { quality: 1, pixelRatio: 2 });
                        resolve(result);
                    } else {
                        resolve(null);
                    }
                }, 100);
            });

            if (!blob) {
                handleDownload();
                return;
            }

            const file = new File([blob], 'kundli-matching.png', { type: 'image/png' });

            if (navigator.share && navigator.canShare({ files: [file] })) {
                const shareLink = `https://bhrigugpt.com/kundli-matching/${language}`;
                await navigator.share({
                    files: [file],
                    title: t.shareTitle,
                    text: `${person1Name} & ${person2Name} - ${t.shareDescription}\n\n${shareLink}`
                });
            } else {
                handleDownload();
            }
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                handleDownload();
            }
        }
    };

    const handleDownload = () => {
        convertToPng();
    };

    const handleCopy = async () => {
        try {
            const blob = await htmlToImageToBlob(cardRef.current!, { quality: 1, pixelRatio: 2 });

            if (!blob) {
                toast.error(t.shareError);
                return;
            }

            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob })
            ]);
            setCopied(true);
            toast.success(t.copiedToClipboard);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error(t.shareError);
        }
    };

    const isGenerating = isDownloading || isSharing;

    if (!isOpen) return null;

    const shareableProps = {
        person1Name,
        person2Name,
        totalScore,
        maxScore,
        compatibilityLevel,
        person1Rashi,
        person2Rashi,
        description,
        percentage,
        gradient,
        t
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full max-w-[340px] sm:max-w-sm md:max-w-md relative">
                {/* Close Button - Outside card */}
                <button
                    onClick={onClose}
                    className="absolute -top-10 right-0 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                    <X size={22} />
                </button>

                {/* Main Card */}
                <div className="bg-[#1e1b4b] rounded-[24px] sm:rounded-[32px] overflow-hidden shadow-2xl border border-white/10">
                    {/* Content Area - Responsive height */}
                    <div className="relative" style={{ paddingBottom: '100%' }}>
                        <div className="absolute inset-0 bg-gradient-to-br from-[#1e1b4b] via-[#5b21b6] to-[#4c1d95]" />
                        <div className="absolute top-0 right-0 w-32 h-32 sm:w-48 sm:h-48 bg-purple-600/30 rounded-full blur-[40px] sm:blur-[60px] -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 sm:w-48 sm:h-48 bg-purple-500/30 rounded-full blur-[40px] sm:blur-[60px] translate-y-1/2 -translate-x-1/2" />

                        <div className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: 'radial-gradient(white 1px, transparent 1px)',
                                backgroundSize: '20px 20px'
                            }}
                        />

                        <div className="absolute inset-0 flex flex-col items-center p-4 sm:p-5">
                            {/* Header Label */}
                            <div className="flex items-center gap-1 bg-white/10 border border-white/20 px-2.5 py-1 rounded-full">
                                <Sparkles size={10} className="text-amber-300" />
                                <span className="text-white text-[9px] sm:text-[10px] font-semibold tracking-wide uppercase">{t.shareTitle}</span>
                            </div>

                            {/* Names Row */}
                            <div className="w-full flex items-center justify-center gap-2 mt-3 px-2">
                                <div className="text-center flex-1 min-w-0">
                                    <h3 className="text-xs sm:text-sm font-bold text-white truncate">{person1Name}</h3>
                                    {person1Rashi && <p className="text-white/50 text-[9px] sm:text-[10px]">{person1Rashi}</p>}
                                </div>
                                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/10 flex items-center justify-center border border-white/20 flex-shrink-0">
                                    <Heart size={14} className="text-pink-500 fill-pink-500" />
                                </div>
                                <div className="text-center flex-1 min-w-0">
                                    <h3 className="text-xs sm:text-sm font-bold text-white truncate">{person2Name}</h3>
                                    {person2Rashi && <p className="text-white/50 text-[9px] sm:text-[10px]">{person2Rashi}</p>}
                                </div>
                            </div>

                            {/* Score Circle */}
                            <div className="relative my-2 sm:my-3 flex-shrink-0">
                                <div className={`absolute -inset-2 rounded-full blur-xl ${gradient.bg} opacity-40`} />
                                <div className="relative w-24 h-24 sm:w-28 sm:h-28">
                                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
                                        <circle
                                            cx="50" cy="50" r="42"
                                            fill="none"
                                            stroke="currentColor"
                                            className={gradient.text}
                                            strokeWidth="6"
                                            strokeLinecap="round"
                                            strokeDasharray={`${(percentage / 100) * 264} 264`}
                                            style={{ filter: 'drop-shadow(0 0 6px currentColor)' }}
                                        />
                                    </svg>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <span className="text-[26px] sm:text-[32px] font-bold text-white leading-none">{totalScore}</span>
                                        <span className="text-[8px] sm:text-[9px] text-white/50 font-medium uppercase tracking-wider mt-0.5">{t.outOf} {maxScore}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Compatibility Badge */}
                            <div className={`px-3 py-1 rounded-lg ${gradient.bg} ${gradient.border} border`}>
                                <span className={`text-[10px] sm:text-xs font-bold ${gradient.text}`}>{compatibilityLevel} • {((totalScore / maxScore) * 100).toFixed(1)}%</span>
                            </div>

                            {/* Description Card */}
                            {description && (
                                <div className="mt-2 mx-1 p-2.5 sm:p-3 bg-white/[0.12] backdrop-blur-sm border border-white/20 rounded-xl">
                                    <p className="text-white/90 text-[9px] sm:text-[11px] leading-relaxed text-center">
                                        {description}
                                    </p>
                                </div>
                            )}

                            {/* Footer Branding */}
                            <div className="mt-auto pt-1 sm:pt-2 flex items-center justify-center gap-1.5">
                                <img
                                    src="/bhrigoo_rishi.webp"
                                    alt="BhriguGPT"
                                    className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                                />
                                <div className="flex flex-col">
                                    <span className="text-white/60 text-[10px] sm:text-xs font-bold tracking-wide">BhriguGPT</span>
                                    <span className="text-white/40 text-[6px] sm:text-[7px] uppercase tracking-[0.12em]">AI Astrologer</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="bg-black/40 backdrop-blur-md p-3 sm:p-4 grid grid-cols-3 gap-2 sm:gap-3 border-t border-white/5">
                        <button
                            onClick={handleShare}
                            disabled={isGenerating}
                            className="col-span-1 flex flex-col items-center justify-center gap-1 sm:gap-1.5 p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-white text-black hover:bg-white/90 transition-all active:scale-95 disabled:opacity-50 disabled:scale-100 group"
                        >
                            <Share2 size={18} className="group-hover:-translate-y-0.5 transition-transform duration-300" />
                            <span className="text-[10px] sm:text-xs font-bold">{t.share}</span>
                        </button>

                        <button
                            onClick={handleDownload}
                            disabled={isGenerating}
                            className="col-span-1 flex flex-col items-center justify-center gap-1 sm:gap-1.5 p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-white/5 text-white hover:bg-white/10 transition-all active:scale-95 disabled:opacity-50 disabled:scale-100 border border-white/5"
                        >
                            <Download size={18} className="text-white/70" />
                            <span className="text-[10px] sm:text-xs font-medium text-white/70">{t.download}</span>
                        </button>

                        <button
                            onClick={handleCopy}
                            disabled={isGenerating}
                            className="col-span-1 flex flex-col items-center justify-center gap-1 sm:gap-1.5 p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-white/5 text-white hover:bg-white/10 transition-all active:scale-95 disabled:opacity-50 disabled:scale-100 border border-white/5"
                        >
                            {copied ? <Check size={18} className="text-emerald-400" /> : <Copy size={18} className="text-white/70" />}
                            <span className="text-[10px] sm:text-xs font-medium text-white/70">{copied ? t.copied : t.copy}</span>
                        </button>
                    </div>
                </div>

                {/* Hidden element for image generation - optimized square 400x400 */}
                <div className="absolute -left-[9999px] top-0">
                    <ShareableContent
                        ref={(node) => {
                            (cardRef as any).current = node;
                            if (typeof pngRef === 'function') pngRef(node);
                            if (typeof blobRef === 'function') blobRef(node);
                        }}
                        {...shareableProps}
                    />
                </div>
            </div>
        </div>
    );
};

export default ShareCard;
